import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import {
  ePlanCode,
  ePlanFrequency,
} from 'src/providers/SubscriptionProvider/Subscription.enums';
import { ePlanCurrency } from '../../../../SubscriptionManager.enums';
import { useCancelSubscriptionContext } from '../CancelSubscription.context';
import PauseOfferModal from '../PauseOfferModal/PauseOfferModal.component';
import NotSeeingResultsOfferModal from './NotSeeingResultsOfferModal/NotSeeingResultsOfferModal.component';
import PlanNotSuitableOfferModal from './PlanNotSuitableOfferModal/PlanNotSuitableOfferModal.component';
import PriceTooHighOfferModal from './PriceTooHighOfferModal/PriceTooHighOfferModal.component';
import RetentionOfferErrorModal from './RetentionOfferErrorModal/RetentionOfferErrorModal.component';
import { tRetentionOfferModalBaseProps } from './RetentionOfferModal.types';
import TechnicalProblemsOfferModal from './TechnicalProblemsOfferModal/TechnicalProblemsOfferModal.component';

export type tRetentionOfferModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * Shows the offer the server resolved for the customer's reason, picked by the
 * offer's type — never by plan or billing frequency, which the server already
 * weighed. The reason only chooses the framing: the budget copy for a discount
 * on price, the results program for an extension on results.
 */
export default function RetentionOfferModal(props: tRetentionOfferModalProps) {
  const { isOpen, onClose } = props;
  const {
    cancellationReason,
    retentionOffer,
    retentionFlowError,
    handleAcceptOffer,
    handleFinalizeSubscription,
    handleRetryOffer,
    handleContinueToCancellation,
    handleBackToReasons,
    isLoadingOfferAcceptance,
    isLoadingFinalization,
  } = useCancelSubscriptionContext();

  const { plans } = useSubscriptionContext();

  const baseProps: tRetentionOfferModalBaseProps = {
    isOpen,
    onAcceptOffer: handleAcceptOffer,
    onFinalizeSubscription: handleFinalizeSubscription,
    onBack: handleBackToReasons,
    onClose,
    isLoadingOfferAcceptance,
    isLoadingFinalization,
  };

  // A step that did not complete, or no offer to show, is never rendered as
  // an offer.
  if (retentionFlowError || !retentionOffer) {
    return (
      <RetentionOfferErrorModal
        isOpen={isOpen}
        error={retentionFlowError ?? { stage: 'lookup', code: null }}
        onClose={onClose}
        onRetry={handleRetryOffer}
        onContinueToCancellation={handleContinueToCancellation}
        isLoading={isLoadingOfferAcceptance || isLoadingFinalization}
      />
    );
  }

  switch (retentionOffer.type) {
    case 'DISCOUNT':
      return (
        <PriceTooHighOfferModal
          {...baseProps}
          offer={retentionOffer}
          copyVariant={
            cancellationReason === 'price_too_high' ? 'price' : 'neutral'
          }
        />
      );
    case 'EXTENSION':
      return cancellationReason === 'not_seeing_results' ? (
        <NotSeeingResultsOfferModal {...baseProps} offer={retentionOffer} />
      ) : (
        <TechnicalProblemsOfferModal {...baseProps} offer={retentionOffer} />
      );
    case 'DOWNGRADE': {
      // The downgrade offer carries no price preview; the Basic plan's catalog
      // price is shown when the catalog has loaded, and left out otherwise.
      const basicPlan = plans?.[ePlanFrequency.MONTHLY]?.find(
        (plan) => plan.planCode === ePlanCode.PLAN_BASIC,
      );

      return (
        <PlanNotSuitableOfferModal
          {...baseProps}
          basicPlanPrice={basicPlan ? basicPlan.totalPrice : null}
          basicPlanCurrency={
            basicPlan?.currency === ePlanCurrency.MXN ? 'MXN' : 'USD'
          }
        />
      );
    }
    case 'PLAN_PAUSE':
      return (
        <PauseOfferModal
          isOpen={isOpen}
          offer={retentionOffer}
          onAcceptOffer={handleAcceptOffer}
          onFinalizeSubscription={handleFinalizeSubscription}
          onBack={handleBackToReasons}
          onClose={onClose}
          isLoadingOfferAcceptance={isLoadingOfferAcceptance}
          isLoadingFinalization={isLoadingFinalization}
        />
      );
  }
}
